import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { MessageSquare, Calendar, Clock, Copy, Check, ExternalLink } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { API_URL } from './App';

export default function SharedChatView() {
  const { shareId } = useParams();
  const [searchParams] = useSearchParams();
  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);

  useEffect(() => {
    fetchSharedChat();
  }, [shareId]);

  const fetchSharedChat = async () => {
    setLoading(true);
    const token = searchParams.get('token');
    
    try {
      const response = await fetch(`${API_URL}/shared/chat/${shareId}?token=${token}`);
      const data = await response.json();
      
      if (data.success) {
        setChat(data.chat);
      } else {
        setError(data.error || 'Failed to load shared chat');
      }
    } catch (error) {
      setError('Failed to load shared chat');
    } finally {
      setLoading(false);
    }
  };

  const copyMessage = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-lg bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold mb-2">Chat Not Available</h2>
          <p className="text-gray-400">{error}</p>
          <p className="text-sm text-gray-500 mt-4">
            The link may have expired or been removed by the owner.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold line-clamp-2">{chat.title}</h1>
            <a
              href="/"
              className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-colors"
            >
              Try AI Genesis
            </a>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-400">
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-1" />
              {new Date(chat.created_at).toLocaleDateString()}
            </div>
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              {new Date(chat.created_at).toLocaleTimeString()}
            </div>
          </div>

          <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <p className="text-sm text-yellow-400 flex items-center">
              <ExternalLink className="w-4 h-4 mr-2" />
              This is a shared chat. Sign up for AI Genesis to create your own chats and generate images/videos.
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {chat.messages.map((msg, index) => (
            <div
              key={index}
              className={`flex gap-4 ${msg.role === 'user' ? 'justify-end' : ''}`}
            >
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0">
                  <img 
                    src="https://app.gospelflixstream.com/aigenesis.jpg" 
                    alt="AI Assistant"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              <div className={`max-w-[80%] ${msg.role === 'user' ? 'order-first' : ''}`}>
                <div className={`p-4 rounded-lg ${
                  msg.role === 'user'
                    ? 'bg-purple-600/20 border border-purple-500/30'
                    : 'bg-gray-800/50'
                }`}>
                  <ReactMarkdown className="prose prose-invert max-w-none text-sm">
                    {msg.content}
                  </ReactMarkdown>
                </div>

                <div className="flex items-center justify-between mt-2 px-1">
                  <span className="text-xs text-gray-500">
                    {msg.role === 'user' ? 'You' : 'AI Genesis'}
                  </span>
                  <button
                    onClick={() => copyMessage(msg.content, index)}
                    className="text-xs text-gray-400 hover:text-white transition-colors p-1"
                  >
                    {copiedIndex === index ? (
                      <Check className="w-3 h-3 text-green-400" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )}
                  </button>
                </div>
              </div>

              {msg.role === 'user' && (
                <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-medium">U</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}